import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, ShoppingBag, MessageCircle } from "lucide-react";
import { useSellerRef } from "@/context/SellerRefContext";

const SHOWN_KEY = "produlimp_seller_welcome_shown";

const SellerWelcomeModal = () => {
  const { seller } = useSellerRef();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!seller) return;
    const lastShownFor = sessionStorage.getItem(SHOWN_KEY);
    if (lastShownFor !== seller.code) {
      // small delay so it feels organic after the page loads
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [seller]);

  const close = () => {
    if (seller) sessionStorage.setItem(SHOWN_KEY, seller.code);
    setOpen(false);
  };

  if (!seller || !open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 fade-in" onClick={close} data-testid="seller-welcome-modal">
      <div
        className="relative bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative top stripe */}
        <div className="h-3 bg-gradient-to-r from-blue-600 via-green-500 to-red-500" />
        <button onClick={close} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 text-slate-500 z-10" aria-label="Cerrar" data-testid="seller-welcome-close">
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 text-center">
          {/* Avatar */}
          <div className="relative inline-flex items-center justify-center mb-5">
            <div className="absolute inset-0 bg-green-200 rounded-full blur-xl opacity-60" />
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center text-white shadow-lg">
              <span className="font-heading font-extrabold text-3xl">{getInitials(seller.name)}</span>
            </div>
            <span className="absolute -bottom-1 -right-1 bg-yellow-400 text-slate-900 rounded-full p-1.5 shadow-md">
              <Sparkles className="w-3.5 h-3.5" />
            </span>
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-600">¡Bienvenido a Produlimp BV!</p>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight mt-2 text-slate-900">
            Te atiende
          </h2>
          <p className="font-heading text-3xl font-extrabold text-green-700 mt-1">{seller.name}</p>
          <p className="text-sm text-slate-500 mt-2">Código de vendedor <span className="font-mono font-bold text-slate-700">{seller.code}</span> · ya quedó asignado a tu compra</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <Link to="/shop" onClick={close} className="bg-green-600 hover:bg-green-700 text-white rounded-full px-4 py-3 font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="seller-welcome-shop">
              <ShoppingBag className="w-4 h-4" /> Ver productos
            </Link>
            <a
              href={`https://wa.me/${seller.whatsapp}?text=${encodeURIComponent(`Hola ${seller.name}! Quería hacerte una consulta.`)}`}
              target="_blank" rel="noreferrer"
              onClick={close}
              className="border-2 border-slate-200 hover:border-green-500 hover:text-green-700 text-slate-700 rounded-full px-4 py-3 font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="seller-welcome-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> Saludarlo
            </a>
          </div>

          <p className="text-xs text-slate-400 mt-5">Tu pedido va directo a su WhatsApp · respuesta inmediata</p>
        </div>
      </div>
    </div>
  );
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "PB";
};

export default SellerWelcomeModal;
